import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  List, Tag, Button, Radio, Spin, Empty, Modal, Input, message, Avatar, Space, Tooltip,
} from "antd";
import {
  UserOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined,
  QuestionCircleOutlined, ReloadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import { API_DOMAIN } from "../constants";
import { QuoteApprovalModal } from "../qv4/QuoteApprovalModal";

dayjs.extend(relativeTime);
dayjs.extend(utc);

const dubaiFakeNow = () => dayjs.utc().add(4, "hour");
const fmtTs = (ts) => {
  if (!ts) return "—";
  const d = dayjs.utc(ts.replace(" ", "T"));
  return `${d.format("DD MMM · hh:mm A")} · ${d.from(dubaiFakeNow())}`;
};

const STATUS_COLOR = {
  pending: "orange",
  approved: "green",
  rejected: "red",
  cancelled: "default",
  request_changes: "blue",
  escalated: "purple",
};

const ACTION_COLOR = {
  quote_send: "blue",
  discount_approval: "purple",
  purchase_before_payment: "cyan",
  refund_payment: "orange",
  delete_payment: "red",
};

export function RequestsTab({ open, userMap, authState, onRequestDecided }) {
  const head = useMemo(() => ({ headers: { accessToken: localStorage.getItem("accessToken") } }), []);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [userPower, setUserPower] = useState({ priority: 0, level: null });
  const [rulesMap, setRulesMap] = useState({});

  // Quote approval modal state
  const [quoteTarget, setQuoteTarget] = useState(null);

  // Generic action modal state
  const [actionTarget, setActionTarget] = useState(null);
  const [actionType, setActionType] = useState("approve");
  const [actionReason, setActionReason] = useState("");
  const [actionOverridePriority, setActionOverridePriority] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const userId = authState?.id;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [reqRes, powerRes, rulesRes] = await Promise.all([
        axios.get(`${API_DOMAIN}/approval-requests`, head),
        userId ? axios.get(`${API_DOMAIN}/users/${userId}/approval-power`, { params: { userId }, ...head }) : Promise.resolve(null),
        axios.get(`${API_DOMAIN}/approval-rules`, head),
      ]);
      setRequests(reqRes.data?.data ?? reqRes.data ?? []);
      if (powerRes) setUserPower(powerRes.data?.data ?? { priority: 0, level: null });
      const rm = {};
      (rulesRes.data?.data ?? rulesRes.data ?? []).forEach(r => { rm[r.action_key] = r; });
      setRulesMap(rm);
    } catch { message.error("Failed to load approval requests"); }
    finally { setLoading(false); }
  }, [userId]); // eslint-disable-line

  useEffect(() => { if (open) fetchAll(); }, [open]); // eslint-disable-line

  const filtered = useMemo(() => {
    if (filter === "pending") return requests.filter(r => r.status === "pending");
    if (filter === "decided") return requests.filter(r => r.status !== "pending");
    return requests;
  }, [requests, filter]);

  const canAct = (req) => userPower.priority >= (req.required_priority ?? 0) && req.status === "pending";

  const handleReviewQuote = (req) => setQuoteTarget(req);

  const openGenericAction = (req, type) => {
    setActionTarget(req);
    setActionType(type);
    setActionReason("");
    setActionOverridePriority(null);
  };

  const submitGenericAction = async () => {
    if ((actionType === "reject" || actionType === "request_changes" || actionType === "cancel" || actionType === "escalate") && !actionReason.trim()) {
      message.warning("Please enter a reason");
      return;
    }
    setActionLoading(true);
    try {
      const body = {
        userId,
        action: actionType,
        decision_reason: actionReason.trim() || null,
      };
      if (actionType === "escalate" && actionOverridePriority) {
        body.override_priority = actionOverridePriority;
      }
      await axios.post(`${API_DOMAIN}/approval-requests/${actionTarget.id}/action`, body, head);
      message.success(`Request ${actionType}d`);
      setRequests(prev => prev.map(r =>
        r.id === actionTarget.id ? { ...r, status: actionType === "approve" ? "approved" : actionType === "reject" ? "rejected" : actionType } : r
      ));
      onRequestDecided?.(actionTarget.id, actionType);
      setActionTarget(null);
    } catch (e) {
      message.error(e?.response?.data?.message || "Action failed");
    } finally { setActionLoading(false); }
  };

  const handleQuoteDone = (result, approvalId) => {
    setRequests(prev => prev.map(r =>
      r.id === approvalId ? { ...r, status: result } : r
    ));
    onRequestDecided?.(approvalId, result);
    setQuoteTarget(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, height: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
        <Radio.Group value={filter} onChange={e => setFilter(e.target.value)} size="small" buttonStyle="solid">
          <Radio.Button value="pending">Pending</Radio.Button>
          <Radio.Button value="decided">Decided</Radio.Button>
          <Radio.Button value="all">All</Radio.Button>
        </Radio.Group>
        <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={fetchAll}>Refresh</Button>
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spin /></div>
      ) : filtered.length === 0 ? (
        <Empty description={`No ${filter} requests`} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 40 }} />
      ) : (
        <div style={{ overflowY: "auto", flex: 1 }}>
          <List
            dataSource={filtered}
            split={false}
            renderItem={req => {
              const rule = rulesMap[req.action_key];
              const submitterName = req.submitted_by ? (userMap?.[req.submitted_by] ?? `User #${req.submitted_by}`) : "—";
              const deciderName = req.decided_by ? (userMap?.[req.decided_by] ?? `User #${req.decided_by}`) : null;
              const isPending = req.status === "pending";
              const userCanAct = canAct(req);
              const isQuoteSend = req.action_key === "quote_send";

              return (
                <List.Item style={{ padding: "6px 0" }}>
                  <div style={{
                    width: "100%",
                    border: `1px solid ${isPending ? "#ffe58f" : req.status === "approved" ? "#b7eb8f" : req.status === "rejected" ? "#ffccc7" : "#d9d9d9"}`,
                    background: isPending ? "#fffbe6" : req.status === "approved" ? "#f6ffed" : req.status === "rejected" ? "#fff2f0" : "#fafafa",
                    borderRadius: 8,
                    padding: "10px 12px",
                  }}>
                    {/* Header row */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                      <Space size={4} wrap>
                        <Tag color={ACTION_COLOR[req.action_key] ?? "default"} style={{ margin: 0 }}>
                          {rule?.action_label ?? req.action_key}
                        </Tag>
                        {req.query_id && (
                          <Tag style={{ margin: 0, fontWeight: 600 }}>#{req.query_id}</Tag>
                        )}
                      </Space>
                      <Tag color={STATUS_COLOR[req.status] ?? "default"} style={{ margin: 0, flexShrink: 0 }}>
                        {req.status}
                      </Tag>
                    </div>

                    {/* Meta */}
                    <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>
                      <UserOutlined style={{ marginRight: 4 }} />
                      <span>Submitted by <strong style={{ color: "#595959" }}>{submitterName}</strong></span>
                      <span style={{ margin: "0 6px" }}>·</span>
                      <ClockCircleOutlined style={{ marginRight: 4 }} />
                      {fmtTs(req.submitted_at)}
                    </div>

                    {/* Required level */}
                    <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 6 }}>
                      Required:{" "}
                      <Tag color="purple" style={{ fontSize: 10, margin: 0 }}>
                        Priority {req.required_priority}
                      </Tag>
                      {userPower.priority > 0 && (
                        <span style={{ marginLeft: 4 }}>
                          · Your power:{" "}
                          <Tag color={userCanAct ? "green" : "default"} style={{ fontSize: 10, margin: 0 }}>
                            Priority {userPower.priority}
                          </Tag>
                        </span>
                      )}
                    </div>

                    {/* Decided info */}
                    {!isPending && deciderName && (
                      <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>
                        {req.status === "approved" ? <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 4 }} /> : <CloseCircleOutlined style={{ color: "#ff4d4f", marginRight: 4 }} />}
                        {req.status} by <strong style={{ color: "#595959" }}>{deciderName}</strong>
                        {req.decided_at && <span> · {fmtTs(req.decided_at)}</span>}
                      </div>
                    )}
                    {!isPending && req.decision_reason && (
                      <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>
                        Reason: {req.decision_reason}
                      </div>
                    )}

                    {/* Action buttons */}
                    {isPending && userCanAct && (
                      <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                        {isQuoteSend ? (
                          <Button type="primary" size="small" onClick={() => handleReviewQuote(req)}>
                            Review Quote
                          </Button>
                        ) : (
                          <>
                            <Button type="primary" size="small" onClick={() => openGenericAction(req, "approve")}>
                              Approve
                            </Button>
                            <Button danger size="small" onClick={() => openGenericAction(req, "reject")}>
                              Reject
                            </Button>
                            <Button size="small" onClick={() => openGenericAction(req, "request_changes")}>
                              Request Changes
                            </Button>
                          </>
                        )}
                        {rule?.allow_override && (
                          <Tooltip title="Escalate to higher approver">
                            <Button size="small" onClick={() => openGenericAction(req, "escalate")}>
                              Escalate
                            </Button>
                          </Tooltip>
                        )}
                      </div>
                    )}
                    {isPending && !userCanAct && (
                      <div style={{ fontSize: 11, color: "#bfbfbf", marginTop: 6, display: "flex", alignItems: "center", gap: 4 }}>
                        <QuestionCircleOutlined />
                        You don't have sufficient approval power for this request
                      </div>
                    )}
                  </div>
                </List.Item>
              );
            }}
          />
        </div>
      )}

      {/* Quote approval modal */}
      <QuoteApprovalModal
        open={!!quoteTarget}
        onClose={() => setQuoteTarget(null)}
        approval={quoteTarget}
        authState={authState}
        userId={userId}
        onDone={handleQuoteDone}
      />

      {/* Generic action modal */}
      <Modal
        open={!!actionTarget}
        title={`${actionType.charAt(0).toUpperCase() + actionType.slice(1).replace("_", " ")} Request`}
        onCancel={() => setActionTarget(null)}
        onOk={submitGenericAction}
        confirmLoading={actionLoading}
        okButtonProps={{ danger: actionType === "reject" || actionType === "cancel" }}
        okText={actionType.charAt(0).toUpperCase() + actionType.slice(1).replace("_", " ")}
        destroyOnHidden
      >
        {actionTarget && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
            <div style={{ fontSize: 12, color: "#595959" }}>
              <Tag color={ACTION_COLOR[actionTarget?.action_key] ?? "default"}>
                {rulesMap[actionTarget?.action_key]?.action_label ?? actionTarget?.action_key}
              </Tag>
              {actionTarget?.query_id && <Tag>#{actionTarget.query_id}</Tag>}
            </div>
            {actionType === "escalate" && (
              <div>
                <div style={{ fontSize: 12, marginBottom: 4, color: "#595959" }}>Override Priority (optional)</div>
                <Input
                  type="number"
                  placeholder="e.g. 30"
                  value={actionOverridePriority ?? ""}
                  onChange={e => setActionOverridePriority(e.target.value ? Number(e.target.value) : null)}
                />
              </div>
            )}
            <div>
              <div style={{ fontSize: 12, marginBottom: 4, color: "#595959" }}>
                Reason {(actionType === "approve") ? "(optional)" : "(required)"}
              </div>
              <Input.TextArea
                rows={3}
                placeholder="Enter reason..."
                value={actionReason}
                onChange={e => setActionReason(e.target.value)}
                autoFocus
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
